import { useMemo } from "react";
import {
  Button,
  Card,
  Recommendation,
  RecommendationDialogItems,
  Swiper,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Dialog, ScrollView, Text, useMedia, View } from "tamagui";

import { chunkArray } from "~/utils/chunk-array";
import { CallToDoctorButton } from "~/component/feature/paywall/call-to-doctor-button";
import { IconPresent } from "~/component/feature/paywall/icons/icon-present";
import { useAutoScroll } from "~/hooks";

const defaultImage = "/recommendations/default.png";

interface AccordionData {
  id: string;
  title: string;
  content: string;
}

interface DialogData {
  tag: string | undefined;
  tagColor: string | undefined;
  title: string;
  markdown: string;
  accordion: AccordionData[] | undefined;
}

interface RecommendationData {
  id: string;
  text: string;
  image?: string;
  dialog: DialogData;
  tag: string | undefined;
  tagColor: string | undefined;
  doctorProposal?: {
    title: string;
    phoneNumber: string;
  };
}

type RecommendationsLayout = "home" | "results";

const resolveChunkSize = (isMd: boolean, layout: RecommendationsLayout) => {
  return isMd && layout === "results" ? 2 : 3;
};

const DoctorProposalBlock = ({ title }: { title?: string }) => {
  const { sm } = useMedia();

  if (!title) {
    return null;
  }

  return (
    <View
      marginBottom="1$"
      flexDirection={sm ? "column" : "row"}
      gap={"$2"}
      marginTop={"$0.5"}
      padding={"24px"}
      backgroundColor={"#FFF6FB"}
      borderRadius={"$4"}
      borderColor={"#FDA5D5"}
      borderWidth={2}
    >
      <View>
        <IconPresent />
      </View>
      <Text fontSize={18} fontFamily={"$body"}>
        Щоб додаткова підтримка була завжди доступною для вас, після запису до
        фахівця ми відкриємо вам{" "}
        <Text color={"#CD4870"}>
          безкоштовний доступ до діагностики Healthy Mind на 3 місяці.
        </Text>
      </Text>
    </View>
  );
};

export const Recommendations: React.FC<{
  data?: RecommendationData[];
  layout?: RecommendationsLayout;
}> = ({ data, layout = "home" }) => {
  const { t } = useTranslation("results");
  const { md } = useMedia();

  const { trackHeightValue, onScroll, scrollRef, resetOnClose } =
    useAutoScroll();

  const formattedData = useMemo(
    () => chunkArray(data ?? [], resolveChunkSize(md, layout)),
    [data, md, layout],
  );

  if (!data) return null;

  return (
    <View flex={1} alignSelf="stretch" gap={"$1.5"} width={"100%"}>
      {formattedData.map((item, index) => (
        <View
          justifyContent="center"
          flex={resolveChunkSize(md, layout)}
          flexDirection="row"
          key={index}
          gap={"$1.5"}
        >
          {item.map(
            ({ text, tag, tagColor, dialog, id, image, doctorProposal }, i) => (
              <Recommendation.Container
                maxWidth={306}
                borderColor={doctorProposal ? "#F9709A" : undefined}
                backgroundColor={"$warm-snow-500"}
                data-id={`recommendation-container-${i}`}
                width={
                  resolveChunkSize(md, layout) === 2
                    ? "calc(50% - 14px)"
                    : "calc(33% - 8px)"
                }
                justifyContent="space-between"
                key={id}
              >
                <View alignItems="center" gap={"$4"}>
                  <Recommendation.Image path={image ?? defaultImage} />
                  <View gap={"$1"}>
                    {tag && (
                      <Recommendation.Tag
                        data-id={`recommendation-tag-${id}`}
                        rounded={"lg"}
                        backgroundColor={tagColor ?? "$pinkmain-500"}
                        color={"white"}
                      >
                        {tag}
                      </Recommendation.Tag>
                    )}
                    <Recommendation.Text data-id={`recommendation-text-${id}`}>
                      {text}
                    </Recommendation.Text>
                  </View>
                </View>

                <Dialog>
                  <Dialog.Trigger asChild>
                    <Recommendation.Button
                      data-id={`recommendation-button`}
                      maxHeight={"$6"}
                    >
                      <Text
                        color="$gray-primary-900"
                        fontFamily="$subtitle"
                        fontSize="$3"
                        fontWeight="$5"
                        textAlign="center"
                      >
                        {t("recommendations.open")}
                      </Text>
                    </Recommendation.Button>
                  </Dialog.Trigger>

                  <Dialog.Portal>
                    <Dialog.Overlay
                      zIndex={0}
                      key={"recommendations-dialog-overlay"}
                      style={{
                        backgroundColor: "#6A696966",
                        WebkitBackdropFilter: "blur(10px)",
                        backdropFilter: "blur(10px)",
                      }}
                    />
                    <RecommendationDialogItems.Container
                      data-id={`recommendation-dialog-container`}
                    >
                      <RecommendationDialogItems.Image
                        path={image ?? defaultImage}
                      />

                      <Card
                        gap={"$5"}
                        variant="borderless"
                        backgroundColor={"white"}
                        padding={"$4"}
                        data-id={`recommendation-dialog-content-${id}`}
                      >
                        <ScrollView
                          scrollEventThrottle={16}
                          onScroll={onScroll}
                          ref={scrollRef}
                          onContentSizeChange={(_, height) =>
                            trackHeightValue(height)
                          }
                          paddingBottom={"$2"}
                          maxHeight={400}
                        >
                          <View gap={"$1"}>
                            {dialog.tag && (
                              <RecommendationDialogItems.Tag
                                data-id={`recommendation-dialog-tag-${id}`}
                                color="white"
                                rounded={"lg"}
                                backgroundColor={
                                  dialog.tagColor ?? "$pinkmain-500"
                                }
                                alignSelf="flex-start"
                              >
                                {dialog.tag}
                              </RecommendationDialogItems.Tag>
                            )}
                            <RecommendationDialogItems.Title
                              data-id={`recommendation-dialog-title-${id}`}
                            >
                              {dialog.title}
                            </RecommendationDialogItems.Title>
                          </View>
                          <View>
                            <View
                              data-id={`recommendation-dialog-description-${id}`}
                            >
                              <RecommendationDialogItems.RenderMd
                                md={dialog.markdown}
                              />
                            </View>

                            <RecommendationDialogItems.Accordion
                              data-id={`recommendation-dialog-accordion-${id}`}
                              data={dialog?.accordion ?? []}
                            >
                              {({ content }) => (
                                <RecommendationDialogItems.RenderMd
                                  md={content}
                                />
                              )}
                            </RecommendationDialogItems.Accordion>
                          </View>
                          <DoctorProposalBlock title={doctorProposal?.title} />
                        </ScrollView>
                      </Card>
                      {doctorProposal?.phoneNumber !== undefined ? (
                        <CallToDoctorButton
                          phoneNumber={doctorProposal.phoneNumber}
                        />
                      ) : (
                        <Dialog.Close asChild>
                          <Button
                            data-id={`recommendation-dialog-close-${id}`}
                            variant="primary"
                            rounded="lg"
                            maxWidth={300}
                            width="100%"
                            alignSelf="center"
                            size="lg"
                            onPress={resetOnClose}
                          >
                            {t("recommendations.close")}
                          </Button>
                        </Dialog.Close>
                      )}
                    </RecommendationDialogItems.Container>
                  </Dialog.Portal>
                </Dialog>
              </Recommendation.Container>
            ),
          )}
        </View>
      ))}
    </View>
  );
};

const getChunkLength = (length: number) => {
  if (length <= 3) return length;
  return Math.floor(length / 2);
};

export const RecommendationMobile: React.FC<{
  data?: RecommendationData[];
}> = ({ data }) => {
  const { t } = useTranslation("results");
  const { sm } = useMedia();

  const { trackHeightValue, onScroll, scrollRef, resetOnClose } =
    useAutoScroll();

  const recommedation = useMemo(
    () => chunkArray(data ?? [], getChunkLength(data?.length ?? 0)),
    [data],
  );
  if (!data) return null;

  return (
    <View
      data-id="recommendation-mobile-wrapper"
      width={"100%"}
      alignItems="center"
      gap={"$3"}
    >
      {recommedation.map((items, index) => (
        <Swiper.Provider key={index} data={items}>
          <Swiper.Container
            data-id={`recommendation-mobile-swiper-${index}`}
            contentContainerStyle={{ gap: 10 }}
            width={"100%"}
            maxWidth={368}
          >
            {({ item, index }: { item: RecommendationData; index: number }) => (
              <Swiper.Item
                key={index}
                paddingBottom={"$0.5"}
                data-id={`recommendation-mobile-item-${item.id}`}
              >
                <Recommendation.Container
                  flex={1}
                  data-id={`recommendation-mobile-container-${item.id}`}
                  borderColor={item.doctorProposal ? "#F9709A" : undefined}
                  justifyContent="space-between"
                  padding={"$3"}
                  gap={"$3"}
                >
                  <View alignItems="center" gap={"$3"}>
                    <Recommendation.Image path={item.image ?? defaultImage} />
                    <View gap={"$1"}>
                      {item.tag && (
                        <Recommendation.Tag
                          data-id={`recommendation-mobile-tag-${item.id}`}
                          backgroundColor={item.tagColor ?? "$pinkmain-500"}
                          rounded={"lg"}
                          color={"white"}
                        >
                          {item.tag}
                        </Recommendation.Tag>
                      )}
                      <Recommendation.Text
                        data-id={`recommendation-mobile-text-${item.id}`}
                        font="md"
                      >
                        {item.text}
                      </Recommendation.Text>
                    </View>
                  </View>

                  <Dialog>
                    <Dialog.Trigger asChild>
                      <Recommendation.Button
                        data-id={`recommendation-mobile-button-${item.id}`}
                      >
                        {t("recommendations.open")}
                      </Recommendation.Button>
                    </Dialog.Trigger>
                    <Dialog.Portal
                      data-id={`recommendation-mobile-dialog-${item.id}`}
                    >
                      <Dialog.Overlay
                        data-id={`recommendation-mobile-dialog-overlay-${item.id}`}
                        key={"recommendation-dialgo-overlay-mobile"}
                        zIndex={0}
                        style={{
                          backgroundColor: "#6A696966",
                          WebkitBackdropFilter: "blur(10px)",
                          backdropFilter: "blur(10px)",
                        }}
                      />
                      <RecommendationDialogItems.Container
                        data-id={`recommendation-mobile-dialog-container-${item.id}`}
                        $sm={{
                          paddingHorizontal: "$2",
                        }}
                      >
                        <RecommendationDialogItems.Image
                          path={item.image ?? defaultImage}
                        />

                        <Card
                          gap={"$2"}
                          variant="borderless"
                          backgroundColor={"white"}
                          padding={"$4"}
                          data-id={`recommendation-mobile-dialog-content-${item.id}`}
                        >
                          <ScrollView
                            scrollEventThrottle={16}
                            onScroll={onScroll}
                            ref={scrollRef}
                            onContentSizeChange={(_, height) =>
                              trackHeightValue(height)
                            }
                            maxHeight={200}
                          >
                            <View gap={"$1"}>
                              {item.dialog.tag && (
                                <RecommendationDialogItems.Tag
                                  data-id={`recommendation-mobile-dialog-tag-${item.id}`}
                                  rounded={"lg"}
                                  backgroundColor={
                                    item.dialog.tagColor ?? "$pinkmain-500"
                                  }
                                  color="white"
                                  alignSelf="flex-start"
                                >
                                  {item.dialog.tag}
                                </RecommendationDialogItems.Tag>
                              )}
                              <RecommendationDialogItems.Title
                                data-id={`recommendation-mobile-dialog-title-${item.id}`}
                              >
                                {item.dialog.title}
                              </RecommendationDialogItems.Title>
                            </View>
                            <View>
                              <RecommendationDialogItems.RenderMd
                                md={item.dialog.markdown}
                              />

                              <RecommendationDialogItems.Accordion
                                data-id={`recommendation-mobile-dialog-accordion-${item.id}`}
                                data={item.dialog.accordion ?? []}
                              >
                                {({ content }) => (
                                  <RecommendationDialogItems.RenderMd
                                    md={content}
                                  />
                                )}
                              </RecommendationDialogItems.Accordion>
                              <DoctorProposalBlock
                                title={item.doctorProposal?.title}
                              />
                            </View>
                          </ScrollView>
                        </Card>

                        {item.doctorProposal?.phoneNumber !== undefined ? (
                          <CallToDoctorButton
                            phoneNumber={item.doctorProposal.phoneNumber}
                          />
                        ) : (
                          <Dialog.Close asChild>
                            <Button
                              data-id={`recommendation-mobile-dialog-close-${item.id}`}
                              variant="primary"
                              rounded="lg"
                              maxWidth={300}
                              width="100%"
                              alignSelf="center"
                              size={sm ? "md" : "lg"}
                              onPress={resetOnClose}
                            >
                              {t("recommendations.close")}
                            </Button>
                          </Dialog.Close>
                        )}
                      </RecommendationDialogItems.Container>
                    </Dialog.Portal>
                  </Dialog>
                </Recommendation.Container>
              </Swiper.Item>
            )}
          </Swiper.Container>

          <Swiper.BulletsContainer
            data-id={`recommendation-mobile-bullets-${index}`}
          >
            {(index) => (
              <Swiper.Bullet
                key={index}
                data-id={`recommendation-mobile-bullet-${index}`}
                index={index}
                style={{ width: 80, height: 2, borderRadius: 0 }}
                transform={(active) => [{ scaleY: active ? 2 : 1 }]}
              />
            )}
          </Swiper.BulletsContainer>
        </Swiper.Provider>
      ))}
    </View>
  );
};
